import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE } from '../utils/api';
import AdverseEventForm from './AdverseEventForm';
import AdverseEventFollowUp from './AdverseEventFollowUp';
import AdverseEventWizard from './AdverseEventWizard';
import MRTTableAdapter from './MRTTableAdapter';
import { createMRTColumnHelper } from 'material-react-table';
import { useAuth } from '../contexts/AuthContext';

interface ICSR {
  id: number;
  description: string;
  severity: string;
  received_date: string;
  patient_age: number;
  patient_gender: string;
  is_serious: boolean;
  death: boolean;
  life_threatening: boolean;
  hospitalization: boolean;
  significant_disability: boolean;
  congenital_anomaly: boolean;
  required_intervention: boolean;
  significant_disfigurement?: boolean;
  infection?: boolean;
  product_id: number;
  product_name?: string;
  current_workflow_stage?: string;
  workflow_id?: number;
  is_adverse_event: boolean;
  is_serious_adverse_event: boolean;
  sae_deadline?: string;
  reported_by: string;
  created_at: string;
  report_source: string;
  patient_weight?: number;
  patient_height?: number;
  event_date?: string;
  event_location?: string;
  event_outcome?: string;
  medical_history?: string;
  concomitant_medications?: string;
  allergies?: string;
  reporter_contact?: string;
  lot_number?: string;
  expiration_date?: string;
  product_usage?: string;
  status?: string;
  priority?: string;
  notes?: string;
}

interface User {
  id: number;
  username: string;
  full_name: string;
  role: {
    id: number;
    name: string;
  };
}

const ICSRDashboard: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const [icsrs, setIcsrs] = useState<ICSR[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [workflowStages, setWorkflowStages] = useState<string[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIcsr, setEditingIcsr] = useState<ICSR | null>(null);
  const [success, setSuccess] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [selectedIcsr, setSelectedIcsr] = useState<ICSR | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewingDetail, setViewingDetail] = useState<ICSR | null>(null);
  const [creatingWorkflowId, setCreatingWorkflowId] = useState<number | null>(null);
  // Separate input state from inclusion params used in background fetches
  const [recordIdLookup, setRecordIdLookup] = useState<string>(''); // used by Find-by-ID only
  const [includeRid, setIncludeRid] = useState<string>(''); // used for fetch inclusion params
  

  const fetchIcsrs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      // Also consider last AE ID from sessionStorage on regular fetch
      let storedRid = '';
      let storedAe = '';
      try {
        storedRid = sessionStorage.getItem('LAST_EXPORT_RECORD_ID') || '';
        storedAe = sessionStorage.getItem('LAST_EXPORT_AE_ID') || '';
      } catch {}
      const ridToUse = includeRid || storedRid;
      const ridParam = ridToUse ? `&record_id=${encodeURIComponent(ridToUse)}` : '';
      const aeParam = storedAe ? `&ae_id=${encodeURIComponent(storedAe)}` : '';
      const response = await fetch(`${API_BASE}/adverse-events/enriched?t=${Date.now()}${ridParam}${aeParam}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data) ? data : (data?.data || []);
        // Debug: log enriched payload count and sample
        try {
          console.log('ICSR enriched count:', Array.isArray(list) ? list.length : 0, data);
        } catch {}
        const normalized: ICSR[] = (list as any[]).map((d: any) => {
          const id = Number(d.id);
          const description = d.description || d.title || `Adverse Event #${id}`;
          const severity = String(d.severity || d.type || 'mild').toLowerCase();
          const received_date = d.received_date || d.created_at;
          const product_id = d.product_id ?? 0;
          const product_name = d.product_name ?? (String(d.report_source || '').toLowerCase() === 'literature' ? 'Literature' : undefined);
          const reported_by = d.reported_by ?? '';
          const rawStage = d.current_workflow_stage ?? d.workflow_stage ?? '';
          const stageLower = typeof rawStage === 'string' ? rawStage : '';
          const death = Boolean(d.death);
          const life_threatening = Boolean(d.life_threatening);
          const hospitalization = Boolean(d.hospitalization);
          const significant_disability = Boolean(d.significant_disability);
          const congenital_anomaly = Boolean(d.congenital_anomaly);
          const required_intervention = Boolean(d.required_intervention);
          const significant_disfigurement = Boolean((d as any).significant_disfigurement);
          const infection = Boolean((d as any).infection);
          const derivedIsSAE = Boolean(
            (d.is_serious_adverse_event ?? d.is_serious) ||
            death || life_threatening || hospitalization || significant_disability || congenital_anomaly || required_intervention || significant_disfigurement || infection
          );
          return {
            id,
            description,
            severity,
            received_date,
            patient_age: d.patient_age ?? 0,
            patient_gender: d.patient_gender ?? '',
            is_serious: Boolean(d.is_serious),
            death,
            life_threatening,
            hospitalization,
            significant_disability,
            congenital_anomaly,
            required_intervention,
            significant_disfigurement,
            infection,
            product_id,
            product_name,
            current_workflow_stage: stageLower,
            workflow_id: d.workflow_id,
            is_adverse_event: Boolean(d.is_adverse_event ?? true),
            is_serious_adverse_event: derivedIsSAE,
            sae_deadline: d.sae_deadline,
            reported_by,
            created_at: d.created_at ?? new Date().toISOString(),
            report_source: d.report_source ?? 'unknown',
          } as ICSR;
        });
        // Merge: keep existing rows and upsert fetched rows by id (like workflow tab)
        setIcsrs((prev) => {
          const map = new Map<number, ICSR>();
          // Keep all previous items
          (prev || []).forEach(r => map.set(r.id, r));
          // Upsert new/updated items
          normalized.forEach(r => map.set(r.id, r));
          // Return sorted array (newest first)
          const out = Array.from(map.values());
          try {
            out.sort((a, b) => {
              const dateComp = String(b.created_at || '').localeCompare(String(a.created_at || ''));
              return dateComp !== 0 ? dateComp : (b.id - a.id);
            });
          } catch {}
          try { console.log('[ICSR] After fetch merge, rows:', out.length, out.map(x => x.id)); } catch {}
          return out;
        });
      } else {
        console.error('Failed to fetch ICSRs:', response.status);
      }
    } catch (error) {
      console.error('Error fetching ICSRs:', error);
    } finally {
      setLoading(false);
    }
  }, [includeRid]);

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/users/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, []);

  const fetchWorkflowStages = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/workflow/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkflowStages((data && Array.isArray(data.stages)) ? data.stages : []);
      }
    } catch (error) {
      console.error('Error fetching workflow stages:', error);
    }
  }, []);

  useEffect(() => {
    // Check URL and sessionStorage for last exported identifiers
    let initialRecordId = '';
    let initialAeId = '';
    try {
      const params = new URLSearchParams(window.location.search);
      const urlRid = params.get('record_id') || '';
      const urlAe = params.get('ae_id') || '';
      if (urlRid) {
        initialRecordId = urlRid;
        setIncludeRid(urlRid); // use for fetch, do not prefill input
      }
      if (urlAe) {
        initialAeId = urlAe;
      }
      // Fallback to session storage if URL empty
      if (!initialRecordId) {
        const lastId = sessionStorage.getItem('LAST_EXPORT_RECORD_ID');
        if (lastId) {
          initialRecordId = lastId;
          setIncludeRid(lastId); // use for fetch, do not prefill input
        }
      }
      if (!initialAeId) {
        const lastAe = sessionStorage.getItem('LAST_EXPORT_AE_ID');
        if (lastAe) {
          initialAeId = lastAe;
        }
      }
    } catch {}
    
    // Initial fetch with record_id/ae_id if available
    const doInitialFetch = async () => {
      const token = localStorage.getItem('token');
      const ridParam = initialRecordId ? `&record_id=${encodeURIComponent(initialRecordId)}` : '';
      const aeParam = initialAeId ? `&ae_id=${encodeURIComponent(initialAeId)}` : '';
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE}/adverse-events/enriched?t=${Date.now()}${ridParam}${aeParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const list = Array.isArray(data) ? data : (data?.data || []);
          const normalized: ICSR[] = (list as any[]).map((d: any) => {
            const id = Number(d.id);
            const description = d.description || d.title || `Adverse Event #${id}`;
            const severity = String(d.severity || d.type || 'mild').toLowerCase();
            const received_date = d.received_date || d.created_at;
            const product_id = d.product_id ?? 0;
            const product_name = d.product_name ?? undefined;
            const reported_by = d.reported_by ?? '';
            const rawStage = d.current_workflow_stage ?? d.workflow_stage ?? '';
            const stageLower = typeof rawStage === 'string' ? rawStage : '';
            const death = Boolean(d.death);
            const life_threatening = Boolean(d.life_threatening);
            const hospitalization = Boolean(d.hospitalization);
            const significant_disability = Boolean(d.significant_disability);
            const congenital_anomaly = Boolean(d.congenital_anomaly);
            const required_intervention = Boolean(d.required_intervention);
            const significant_disfigurement = Boolean((d as any).significant_disfigurement);
            const infection = Boolean((d as any).infection);
            const derivedIsSAE = Boolean(
              (d.is_serious_adverse_event ?? d.is_serious) ||
              death || life_threatening || hospitalization || significant_disability || congenital_anomaly || required_intervention || significant_disfigurement || infection
            );
            return {
              id,
              description,
              severity,
              received_date,
              patient_age: d.patient_age ?? 0,
              patient_gender: d.patient_gender ?? '',
              is_serious: Boolean(d.is_serious),
              death,
              life_threatening,
              hospitalization,
              significant_disability,
              congenital_anomaly,
              required_intervention,
              significant_disfigurement,
              infection,
              product_id,
              product_name,
              current_workflow_stage: stageLower,
              workflow_id: d.workflow_id,
              is_adverse_event: Boolean(d.is_adverse_event ?? true),
              is_serious_adverse_event: derivedIsSAE,
              sae_deadline: d.sae_deadline,
              reported_by,
              created_at: d.created_at ?? new Date().toISOString(),
              report_source: d.report_source ?? 'unknown',
            } as ICSR;
          });
          // Upsert merge (do not overwrite) to avoid race with other fetches
          setIcsrs((prev) => {
            const map = new Map<number, ICSR>();
            (prev || []).forEach(r => map.set(r.id, r));
            normalized.forEach(r => map.set(r.id, r));
            const out = Array.from(map.values());
            try {
              out.sort((a, b) => {
                const dateComp = String(b.created_at || '').localeCompare(String(a.created_at || ''));
                return dateComp !== 0 ? dateComp : (b.id - a.id);
              });
            } catch {}
            try { console.log('[ICSR] After initial merge, rows:', out.length, out.map(x => x.id)); } catch {}
            return out;
          });
        }
      } catch (error) {
        console.error('Error in initial fetch:', error);
      } finally {
        setLoading(false);
      }
    };
    
    doInitialFetch();
    fetchUsers();
    fetchWorkflowStages();
    // Removed auto-refresh polling to avoid constant refreshes.
    // Users can click the Refresh button when needed.
    return () => {};
  }, []);  // Empty deps: run once on mount

  const handleCreateWorkflow = async (icsrId: number) => {
    try {
      setCreatingWorkflowId(icsrId);
      const response = await fetch(`${API_BASE}/workflow/cases/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          adverse_event_id: icsrId,
          initial_assignee_id: user?.id || users[0]?.id
        })
      });

      if (response.ok) {
        const workflow = await response.json();
        setIcsrs(prevIcsrs =>
          prevIcsrs.map(icsr =>
            icsr.id === icsrId
              ? { ...icsr, workflow_id: workflow.id, current_workflow_stage: 'draft' }
              : icsr
          )
        );
        setSuccess('Workflow created successfully!');
      } else {
        const txt = await response.text();
        console.error('Failed to create workflow', txt);
        setError(txt || 'Failed to create workflow');
      }
    } catch (error) {
      console.error('Error creating workflow:', error);
      setError('Error creating workflow');
    } finally {
      setCreatingWorkflowId(null);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this ICSR?')) {
      try {
        const response = await fetch(`${API_BASE}/adverse-events/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          setIcsrs((icsrs || []).filter(icsr => icsr.id !== id));
        } else {
          console.error('Failed to delete ICSR');
        }
      } catch (error) {
        console.error('Error deleting ICSR:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const columnHelper = createMRTColumnHelper<ICSR>();
  const columns = [
    columnHelper.accessor(row => row.id, { id: 'record_id', header: 'Record ID', size: 100 }),
    columnHelper.accessor('description', { header: 'Title / Identifier', size: 300 }),
    columnHelper.accessor('severity', { header: 'Severity', size: 120, filterVariant: 'select' }),
    columnHelper.accessor(row => (row.is_adverse_event ? 'Yes' : 'No'), { id: 'is_ae', header: 'Is AE', size: 80, filterVariant: 'select' }),
    columnHelper.accessor(row => (row.is_serious_adverse_event ? 'Yes' : 'No'), { id: 'is_sae', header: 'Is SAE', size: 80, filterVariant: 'select' }),
    columnHelper.accessor('current_workflow_stage', { header: 'Current Task', size: 150, filterVariant: 'select' }),
    columnHelper.accessor(row => (row.product_name || (row.product_id && row.product_id > 0 ? `Product ${row.product_id}` : 'Literature')), { id: 'product', header: 'Product', size: 200 }),
    columnHelper.accessor('reported_by', { header: 'Reported By', size: 150 }),
    columnHelper.accessor(row => (row.received_date ? new Date(row.received_date).toISOString().slice(0,10) : ''), { id: 'received_date', header: 'Received', size: 120, filterVariant: 'date-range' }),
    columnHelper.accessor(row => (row.created_at ? new Date(row.created_at).toISOString().slice(0,10) : ''), { id: 'created_at', header: 'Created', size: 120, filterVariant: 'date-range' }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      size: 280,
      Cell: ({ row }) => (
        <div className="flex justify-end space-x-2">
          <button
            onClick={async (e) => { 
              e.stopPropagation(); 
              try {
                const token = localStorage.getItem('token');
                if (!token) return;
                const headers: any = { 'Authorization': `Bearer ${token}` };

                // Try enriched endpoint first to include product/workflow fields
                let detail: any | null = null;
                try {
                  const res1 = await fetch(`${API_BASE}/adverse-events/enriched?ae_id=${row.original.id}&t=${Date.now()}`, { headers });
                  if (res1.ok) {
                    const data1 = await res1.json();
                    const list = Array.isArray(data1) ? data1 : (data1?.data || []);
                    detail = list.find((x:any) => Number(x.id) === Number(row.original.id)) || list[0] || null;
                  }
                } catch {}

                if (detail) {
                  const viewing: ICSR = {
                    id: Number(detail.id),
                    description: detail.description || detail.title || `Adverse Event #${row.original.id}`,
                    severity: String(detail.severity || detail.type || 'mild').toLowerCase(),
                    received_date: detail.received_date || detail.created_at || '',
                    // Patient
                    patient_age: detail.patient_age ?? 0,
                    patient_gender: detail.patient_gender ?? '',
                    patient_weight: detail.patient_weight ?? undefined,
                    patient_height: detail.patient_height ?? undefined,
                    // Event Details
                    event_date: detail.event_date || undefined,
                    event_location: detail.event_location || undefined,
                    event_outcome: detail.event_outcome || undefined,
                    // SAE Flags
                    is_serious: Boolean(detail.is_serious),
                    death: Boolean(detail.death),
                    life_threatening: Boolean(detail.life_threatening),
                    hospitalization: Boolean(detail.hospitalization),
                    significant_disability: Boolean(detail.significant_disability),
                    congenital_anomaly: Boolean(detail.congenital_anomaly),
                    required_intervention: Boolean(detail.required_intervention),
                    significant_disfigurement: Boolean((detail as any).significant_disfigurement),
                    infection: Boolean((detail as any).infection),
                    // Product
                    product_id: detail.product_id ?? 0,
                    product_name: detail.product_name || (detail.product_id && detail.product_id > 0 ? `Product ${detail.product_id}` : undefined),
                    lot_number: detail.lot_number || undefined,
                    expiration_date: detail.expiration_date || undefined,
                    product_usage: detail.product_usage || undefined,
                    // Workflow
                    current_workflow_stage: detail.current_workflow_stage ?? '',
                    workflow_id: detail.workflow_id,
                    // Classification
                    is_adverse_event: Boolean(detail.is_adverse_event ?? true),
                    is_serious_adverse_event: Boolean(detail.is_serious_adverse_event || detail.is_serious),
                    sae_deadline: detail.sae_deadline,
                    // Reporter
                    reported_by: detail.reported_by ?? '',
                    reporter_contact: detail.reporter_contact || undefined,
                    report_source: detail.report_source ?? 'unknown',
                    // Medical & Notes
                    medical_history: detail.medical_history || undefined,
                    concomitant_medications: detail.concomitant_medications || undefined,
                    allergies: detail.allergies || undefined,
                    status: detail.status || undefined,
                    priority: detail.priority || undefined,
                    notes: detail.notes || undefined,
                    // Meta
                    created_at: detail.created_at ?? '',
                  };
                  setViewingDetail(viewing);
                  return;
                }

                // Fallback to base AE endpoint
                const response = await fetch(`${API_BASE}/adverse-events/${row.original.id}`, { headers: { ...headers, 'Content-Type': 'application/json' } });
                
                if (response.ok) {
                  const ae = await response.json();
                  const viewing: ICSR = {
                    id: ae.id,
                    description: ae.description,
                    severity: (ae.severity || 'mild').toLowerCase(),
                    received_date: ae.received_date || ae.created_at || '',
                    // Patient
                    patient_age: ae.patient_age ?? 0,
                    patient_gender: ae.patient_gender ?? '',
                    patient_weight: ae.patient_weight ?? undefined,
                    patient_height: ae.patient_height ?? undefined,
                    // Event Details
                    event_date: ae.event_date || undefined,
                    event_location: ae.event_location || undefined,
                    event_outcome: ae.event_outcome || undefined,
                    // SAE Flags
                    is_serious: Boolean(ae.is_serious),
                    death: Boolean(ae.death),
                    life_threatening: Boolean(ae.life_threatening),
                    hospitalization: Boolean(ae.hospitalization),
                    significant_disability: Boolean(ae.significant_disability),
                    congenital_anomaly: Boolean(ae.congenital_anomaly),
                    required_intervention: Boolean(ae.required_intervention),
                    significant_disfigurement: Boolean((ae as any).significant_disfigurement),
                    infection: Boolean((ae as any).infection),
                    // Product
                    product_id: ae.product_id ?? 0,
                    product_name: undefined,
                    lot_number: ae.lot_number || undefined,
                    expiration_date: ae.expiration_date || undefined,
                    product_usage: ae.product_usage || undefined,
                    // Workflow
                    current_workflow_stage: '',
                    workflow_id: undefined,
                    // Classification
                    is_adverse_event: Boolean(ae.is_adverse_event ?? true),
                    is_serious_adverse_event: Boolean(ae.is_serious_adverse_event || ae.is_serious),
                    sae_deadline: ae.sae_deadline,
                    // Reporter
                    reported_by: ae.reported_by ?? '',
                    reporter_contact: ae.reporter_contact || undefined,
                    report_source: ae.report_source ?? 'unknown',
                    // Medical & Notes
                    medical_history: ae.medical_history || undefined,
                    concomitant_medications: ae.concomitant_medications || undefined,
                    allergies: ae.allergies || undefined,
                    status: ae.status || undefined,
                    priority: ae.priority || undefined,
                    notes: ae.notes || undefined,
                    // Meta
                    created_at: ae.created_at ?? '',
                  };
                  setViewingDetail(viewing);
                } else {
                  console.error('Failed to fetch adverse event details');
                  setError('Failed to load adverse event details');
                }
              } catch (error) {
                console.error('Error fetching adverse event:', error);
                setError('Error loading adverse event details');
              }
            }}
            className="px-2 py-1 text-sm rounded border border-gray-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 bg-white/80 dark:bg-slate-800/40 hover:bg-gray-50 dark:hover:bg-slate-700"
            title="View"
          >
            View
          </button>
          <button
            onClick={async (e) => { 
              e.stopPropagation(); 
              try {
                const token = localStorage.getItem('token');
                if (!token) return;
                
                const response = await fetch(`${API_BASE}/adverse-events/${row.original.id}`, {
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                  }
                });
                
                if (response.ok) {
                  const fullData = await response.json();
                  setEditingIcsr(fullData);
                  setShowForm(true);
                } else {
                  console.error('Failed to fetch adverse event details');
                  setError('Failed to load adverse event details for editing');
                }
              } catch (error) {
                console.error('Error fetching adverse event:', error);
                setError('Error loading adverse event for editing');
              }
            }}
            className="px-2 py-1 text-sm rounded border border-indigo-400 dark:border-indigo-600 text-indigo-700 dark:text-indigo-300 bg-white/80 dark:bg-slate-800/40 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
            title="Edit"
          >
            Edit
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedIcsr(row.original); setShowFollowUp(true); }}
            className="px-2 py-1 text-sm rounded border border-emerald-400 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-white/80 dark:bg-slate-800/40 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
            title="Follow-up"
          >
            Follow-up
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleCreateWorkflow(row.original.id); }}
            disabled={!!row.original.workflow_id}
            className={`px-2 py-1 text-sm rounded border bg-white/80 dark:bg-slate-800/40 ${row.original.workflow_id ? 'border-gray-200 dark:border-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed' : 'border-blue-400 dark:border-blue-700 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20'}`}
            title={row.original.workflow_id ? 'Workflow exists' : 'Create Workflow'}
          >
            {row.original.workflow_id ? 'Workflow' : 'Create WF'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(row.original.id); }}
            className="px-2 py-1 text-sm rounded border border-red-400 dark:border-red-700 text-red-700 dark:text-red-300 bg-white/80 dark:bg-slate-800/40 hover:bg-red-50 dark:hover:bg-red-900/20"
            title="Delete"
          >
            Delete
          </button>
        </div>
      ),
    }),
  ];

  return (
    <>
    <div className="container mx-auto px-4 py-6">
        {/* Debug counter to quickly see how many rows are loaded */}
        <div className="mb-2 text-sm text-gray-500">ICSRs loaded: {icsrs.length}</div>
        {/* Success/Error Notifications */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md animate-fadeIn mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {success}
              <button onClick={() => setSuccess('')} className="ml-auto text-green-600 hover:text-green-800">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md animate-fadeIn mb-6">
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
              <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100 mb-2">ICSR Management</h1>
              <p className="text-slate-700 dark:text-slate-300">Individual Case Safety Reports - Integrated with Workflow Management</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 mr-2">
                <input
                  value={recordIdLookup}
                  onChange={(e) => setRecordIdLookup(e.target.value)}
                  placeholder="Record ID (e.g. 2025US...)"
                  className="border border-gray-300 dark:border-slate-600 rounded px-2 py-1 text-sm bg-white/90 dark:bg-slate-800/60 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <button
                  onClick={async () => {
                    const rid = (recordIdLookup || '').trim();
                    if (!rid) return;
                    try {
                      const token = localStorage.getItem('token');
                      const r = await fetch(`${API_BASE}/api/v2/records/${encodeURIComponent(rid)}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                      });
                      if (!r.ok) {
                        const t = await r.text();
                        setError(t || `Record ${rid} not found`);
                        return;
                      }
                      const data = await r.json();
                      const ae = data?.adverse_event || {};
                      const row: ICSR = {
                        id: ae.id,
                        description: ae.description || data?.title || `ICSR ${rid}`,
                        severity: (ae.severity || 'mild').toLowerCase(),
                        received_date: ae.created_at || new Date().toISOString(),
                        patient_age: ae.patient_age || 0,
                        patient_gender: ae.patient_gender || '',
                        is_serious: Boolean(ae.is_serious),
                        death: Boolean(ae.death),
                        life_threatening: Boolean(ae.life_threatening),
                        hospitalization: Boolean(ae.hospitalization),
                        significant_disability: Boolean(ae.significant_disability),
                        congenital_anomaly: Boolean(ae.congenital_anomaly),
                        required_intervention: Boolean(ae.required_intervention),
                        significant_disfigurement: Boolean((ae as any).significant_disfigurement),
                        infection: Boolean((ae as any).infection),
                        product_id: ae.product_id,
                        product_name: (data?.product && data?.product.name) || undefined,
                        current_workflow_stage: data?.workflow?.current_stage || '',
                        workflow_id: data?.workflow?.id,
                        is_adverse_event: Boolean(ae.is_adverse_event ?? true),
                        is_serious_adverse_event: Boolean(ae.is_serious_adverse_event || ae.is_serious),
                        sae_deadline: ae.sae_deadline,
                        reported_by: ae.reported_by || 'Literature',
                        created_at: ae.created_at || new Date().toISOString(),
                        report_source: ae.report_source || 'unknown',
                      };
                      setIcsrs((prev) => {
                        const exists = (prev || []).some(p => p.id === row.id);
                        if (exists) return prev;
                        return [row, ...(prev || [])];
                      });
                      setSuccess(`Loaded ICSR ${rid} (AE #${row.id})`);
                    } catch (e) {
                      console.error(e);
                      setError('Failed to load by Record ID');
                    }
                  }}
                  className="bg-white/90 dark:bg-slate-800/60 hover:bg-gray-50 dark:hover:bg-slate-700 border border-gray-300 dark:border-slate-600 text-slate-800 dark:text-slate-200 py-1 px-2 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  Find by ID
                </button>
              </div>
              <button
                onClick={() => fetchIcsrs()}
                className="bg-gray-200 dark:bg-slate-800 hover:bg-gray-300 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200 font-semibold py-2 px-4 rounded-lg shadow-sm transition duration-150 ease-in-out border border-gray-300 dark:border-slate-600"
              >
                Refresh
              </button>
              
              <button
                onClick={() => setShowWizard(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Create from Wizard
              </button>
            </div>
          </div>
        </div>

        {/* Information Board */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-blue-600 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">ICSR Management Overview</h3>
              <div className="text-blue-800 space-y-2">
                <p><strong>What are ICSRs?</strong> Individual Case Safety Reports document adverse events and serious adverse events (SAEs) for regulatory compliance.</p>
                <p><strong>Key Features:</strong></p>
                <ul className="list-disc list-inside ml-4 space-y-1">
                  <li>Create ICSRs using guided wizard or manual form</li>
                  <li>Automatic SAE classification based on FDA criteria</li>
                  <li>Integrated workflow management for case processing</li>
                  <li>Real-time compliance tracking and reporting</li>
                </ul>
                <p><strong>SAE Criteria:</strong> Death, life-threatening, hospitalization, significant disability, congenital anomaly, or required intervention.</p>
              </div>
            </div>
          </div>
        </div>

        {/* ICSR Table */}
        <div className="bg-white rounded-lg shadow-sm border">
          <MRTTableAdapter<ICSR> columns={columns} data={icsrs} />
        </div>

        
      </div>

      {/* Modals */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setShowForm(false); setEditingIcsr(null); }}></div>
          <div className="relative bg-white rounded-xl p-4 sm:p-6 w-full max-w-xl mx-4 shadow-xl max-h-[85vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingIcsr ? 'Edit ICSR' : 'Create New ICSR'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingIcsr(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <AdverseEventForm
              initialData={editingIcsr || undefined}
              onSubmit={async (icsr) => {
                try {
                  const method = editingIcsr ? 'PUT' : 'POST';
                  const url = editingIcsr 
                    ? `${API_BASE}/adverse-events/${editingIcsr.id}`
                    : `${API_BASE}/adverse-events/`;
                  
                  const response = await fetch(url, {
                    method,
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    },
                    body: JSON.stringify(icsr)
                  });

                  if (response.ok) {
                    const savedIcsr = await response.json();
                    
                    if (!editingIcsr && users.length > 0) {
                      try {
                        await handleCreateWorkflow(savedIcsr.id);
                      } catch (workflowError) {
                        console.error('Error creating workflow:', workflowError);
                      }
                    }
                    
                    setShowForm(false);
                    setEditingIcsr(null);
                    fetchIcsrs();
                  } else {
                    console.error('Failed to save ICSR');
                  }
                } catch (error) {
                  console.error('Error saving ICSR:', error);
                }
              }}
            />
          </div>
        </div>
      )}

      {showFollowUp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowFollowUp(false)}></div>
          <div className="relative bg-white rounded-xl p-4 sm:p-6 w-full max-w-xl mx-4 shadow-xl max-h-[85vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Follow-up Management</h2>
              <button
                onClick={() => setShowFollowUp(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <AdverseEventFollowUp
              parentEventId={selectedIcsr?.id || 0}
              onClose={() => setShowFollowUp(false)}
            />
          </div>
        </div>
      )}

      {showWizard && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowWizard(false)}></div>
          <div className="relative bg-white rounded-xl p-4 sm:p-6 w-full max-w-2xl mx-4 shadow-xl max-h-[85vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Create ICSR from Wizard</h2>
              <button
                onClick={() => setShowWizard(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <AdverseEventWizard
              isOpen={showWizard}
              onClose={() => {
                setShowWizard(false);
                fetchIcsrs();
              }}
              onSuccess={() => {
                setShowWizard(false);
                fetchIcsrs();
              }}
            />
          </div>
        </div>
      )}

      {viewingDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setViewingDetail(null)}></div>
          <div className="relative bg-white rounded-xl p-6 w-full max-w-4xl mx-4 shadow-xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-2xl font-semibold text-gray-900">ICSR Details - #{viewingDetail?.id}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {viewingDetail?.is_serious_adverse_event ? 'Serious Adverse Event (SAE)' : 
                   viewingDetail?.is_adverse_event ? 'Adverse Event (AE)' : 'Safety Report'}
                  {viewingDetail?.severity && ` • ${viewingDetail.severity.charAt(0).toUpperCase() + viewingDetail.severity.slice(1)} Severity`}
                </p>
              </div>
              <button onClick={() => setViewingDetail(null)} className="text-gray-500 hover:text-gray-700 text-xl">✕</button>
            </div>

            <div className="space-y-6">
              {/* Event Information */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Event Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600 font-medium">Description:</span> <span className="text-gray-900">{viewingDetail?.description || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Product:</span> <span className="text-gray-900">{viewingDetail?.product_name || (viewingDetail?.product_id ? `Product ${viewingDetail?.product_id}` : '-')}</span></div>
                  <div><span className="text-gray-600 font-medium">Event Date:</span> <span className="text-gray-900">{viewingDetail?.event_date ? new Date(viewingDetail?.event_date as string).toLocaleDateString() : '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Event Location:</span> <span className="text-gray-900">{viewingDetail?.event_location || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Event Outcome:</span> <span className="text-gray-900">{viewingDetail?.event_outcome || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Received Date:</span> <span className="text-gray-900">{(() => { try { const d = viewingDetail?.received_date ? new Date(viewingDetail.received_date as string) : null; return d && !isNaN(d.getTime()) ? d.toLocaleString() : '-'; } catch { return '-'; } })()}</span></div>
                </div>
              </div>

              {/* Patient Information */}
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Patient Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600 font-medium">Age:</span> <span className="text-gray-900">{viewingDetail?.patient_age || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Gender:</span> <span className="text-gray-900">{viewingDetail?.patient_gender || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Weight:</span> <span className="text-gray-900">{viewingDetail?.patient_weight ? `${viewingDetail.patient_weight} kg` : '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Height:</span> <span className="text-gray-900">{viewingDetail?.patient_height ? `${viewingDetail.patient_height} cm` : '-'}</span></div>
                  <div className="md:col-span-2"><span className="text-gray-600 font-medium">Medical History:</span> <span className="text-gray-900">{viewingDetail?.medical_history || '-'}</span></div>
                  <div className="md:col-span-2"><span className="text-gray-600 font-medium">Concomitant Medications:</span> <span className="text-gray-900">{viewingDetail?.concomitant_medications || '-'}</span></div>
                  <div className="md:col-span-2"><span className="text-gray-600 font-medium">Allergies:</span> <span className="text-gray-900">{viewingDetail?.allergies || '-'}</span></div>
                </div>
              </div>

              {/* SAE Criteria */}
              <div className="bg-red-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Serious Adverse Event (SAE) Criteria</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  {['death','life_threatening','hospitalization','significant_disability','congenital_anomaly','required_intervention','significant_disfigurement','infection'].map(criteria => (
                    <div key={criteria} className={`flex items-center p-2 rounded ${(viewingDetail as any)?.[criteria] ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-600'}`}>
                      <span className={`w-2 h-2 rounded-full mr-2 ${(viewingDetail as any)?.[criteria] ? 'bg-red-500' : 'bg-gray-400'}`}></span>
                      <span className="capitalize">{criteria.replace(/_/g, ' ')}</span>
                    </div>
                  ))}
                </div>
                {viewingDetail?.sae_deadline && (
                  <div className="mt-3 p-3 bg-red-100 rounded border border-red-200">
                    <span className="text-red-800 font-semibold">⚠️ SAE Deadline: </span>
                    <span className="text-red-700">{new Date(viewingDetail.sae_deadline as string).toLocaleString()}</span>
                  </div>
                )}
              </div>

              {/* Reporter Information */}
              <div className="bg-green-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Reporter Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600 font-medium">Reported By:</span> <span className="text-gray-900">{viewingDetail?.reported_by || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Contact:</span> <span className="text-gray-900">{viewingDetail?.reporter_contact || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Report Source:</span> <span className="text-gray-900">{viewingDetail?.report_source || '-'}</span></div>
                </div>
              </div>

              {/* Product Information */}
              <div className="bg-yellow-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Product Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600 font-medium">Lot Number:</span> <span className="text-gray-900">{viewingDetail?.lot_number || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Expiration Date:</span> <span className="text-gray-900">{viewingDetail?.expiration_date ? new Date(viewingDetail.expiration_date as string).toLocaleDateString() : '-'}</span></div>
                  <div className="md:col-span-2"><span className="text-gray-600 font-medium">Product Usage:</span> <span className="text-gray-900">{viewingDetail?.product_usage || '-'}</span></div>
                </div>
            </div>

              {/* Workflow Information */}
              <div className="bg-purple-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Workflow Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600 font-medium">Current Stage:</span> <span className="text-gray-900">{viewingDetail?.current_workflow_stage || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Workflow ID:</span> <span className="text-gray-900">{viewingDetail?.workflow_id || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Status:</span> <span className="text-gray-900">{viewingDetail?.status || '-'}</span></div>
                  <div><span className="text-gray-600 font-medium">Priority:</span> <span className="text-gray-900">{viewingDetail?.priority || '-'}</span></div>
                </div>
              </div>

              {/* Additional Notes */}
              {viewingDetail?.notes && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Notes</h3>
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{viewingDetail.notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ICSRDashboard;




